'use client';

import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

interface AuthGateModalProps {
  onClose: () => void;
}

export default function AuthGateModal({ onClose }: AuthGateModalProps) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ background: 'rgba(10,10,20,0.72)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-ink/5 hover:bg-ink/10 transition-colors text-ink/40 hover:text-ink/70 z-10"
          aria-label="Close"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Header gradient */}
        <div className="px-8 pt-8 pb-6 text-center" style={{ background: 'linear-gradient(135deg, #E8FAF9 0%, #E6F9EF 100%)' }}>
          <div className="flex justify-center mb-4">
            <AppLogo size={28} text="QOLLECTIONS" />
          </div>
          <h2 className="text-xl font-bold text-ink mt-3">Welcome to Qollections</h2>
          <p className="text-sm text-ink/50 mt-1">Sign in or create a free account to start managing your collection.</p>
        </div>

        {/* Actions */}
        <div className="px-8 py-7 space-y-3">
          <Link
            href="/auth/signup"
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #4ECDC4, #2ECC71)' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
            </svg>
            Create a free account
          </Link>

          <Link
            href="/auth/login"
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-semibold text-ink border-2 border-ink/10 hover:border-ink/25 transition-all bg-white"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" />
            </svg>
            Sign in to my account
          </Link>
        </div>

        <p className="text-center text-xs text-ink/25 pb-6 tracking-wide">Free to use. No credit card required.</p>
      </div>
    </div>
  );
}
